
import { useState, useEffect, useMemo } from "react";
import { Box, Paper, CircularProgress, Alert, Stack, Typography, FormControl, InputLabel, Select, MenuItem, Chip, IconButton, Tooltip, Button } from "@mui/material";
import { Sync as SyncIcon, SyncDisabled as SyncDisabledIcon, FileDownloadOutlined, RefreshOutlined } from "@mui/icons-material";
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import { useGetUnreachedStatusAnalyticsRange } from "../../../../../../../features/analytics/hooks/useGetUnreachedStatusAnalytics";
import { useExportUnreachedStatusAnalyticsRange } from "../../../../../../../features/analytics/hooks/exports";
import { DynamicTable } from "../components";
import { DATA_TYPE_OPTIONS, generateQueryParams } from "../constants/dataTypeConstants";
import DateRangePicker from '../../../components/date-range-picker';

export const UnreachedCallsTable = ({
  globalDataType,
  syncTimestamp,
  fromDate: controlledFromDate,
  toDate: controlledToDate,
  onFromDateChange,
  onToDateChange
}) => {
  // Default to current month's start and end
  const defaultFrom = controlledFromDate ? dayjs(controlledFromDate) : dayjs().startOf('month');
  const defaultTo = controlledToDate ? dayjs(controlledToDate) : dayjs().endOf('month');

  const [localFromDate, setLocalFromDate] = useState(defaultFrom);
  const [localToDate, setLocalToDate] = useState(defaultTo);
  const [isLocalOverride, setIsLocalOverride] = useState(false);

  const defaultDataType = globalDataType || (DATA_TYPE_OPTIONS[0]?.label ?? 'All');
  const [localDataType, setLocalDataType] = useState(defaultDataType);

  // Sync with global data type when it changes (unless using local override)
  useEffect(() => {
    if (!isLocalOverride && globalDataType) {
      setLocalDataType(globalDataType);
    }
  }, [globalDataType, isLocalOverride]);

  useEffect(() => {
    if (controlledFromDate) {
      setLocalFromDate(dayjs(controlledFromDate));
    }
  }, [controlledFromDate]);

  useEffect(() => {
    if (controlledToDate) {
      setLocalToDate(dayjs(controlledToDate));
    }
  }, [controlledToDate]);

  // Reset to global sync when sync timestamp changes
  useEffect(() => {
    if (globalDataType) setLocalDataType(globalDataType);
    setIsLocalOverride(false);
  }, [syncTimestamp, globalDataType]);

  const handleLocalFromDateChange = (date) => {
    const nextDate = date ? dayjs(date) : defaultFrom;
    setLocalFromDate(nextDate);
    if (onFromDateChange) {
      onFromDateChange(nextDate);
    }
  };

  const handleLocalToDateChange = (date) => {
    const nextDate = date ? dayjs(date) : defaultTo;
    setLocalToDate(nextDate);
    if (onToDateChange) {
      onToDateChange(nextDate);
    }
  };

  const handleLocalDataTypeChange = (event) => {
    setLocalDataType(event.target.value);
    setIsLocalOverride(true);
  };

  const toggleSync = () => {
    if (isLocalOverride) {
      // Reset to global
      setLocalDataType(globalDataType);
      setIsLocalOverride(false);
    } else {
      // Enable local override (keep current value)
      setIsLocalOverride(true);
    }
  };

  // Generate query parameters based on local settings
  const localQueryParams = useMemo(() => {
    const params = {
      from: localFromDate.format('YYYY-MM-DD'),
      to: localToDate.format('YYYY-MM-DD'),
    };
    return generateQueryParams(params, localDataType);
  }, [localFromDate, localToDate, localDataType]);

  // Use the enhanced analytics hook with query parameters
  const {
    data: analyticsData,
    isFetching,
    isError,
    error,
    refetch
  } = useGetUnreachedStatusAnalyticsRange(localQueryParams, {
    enabled: true,
    staleTime: 1000 * 60 * 2, // 2 minutes
    cacheTime: 1000 * 60 * 5, // 5 minutes
    fetchKey: `${localDataType}-${localFromDate.format('YYYY-MM-DD')}-${localToDate.format('YYYY-MM-DD')}`,
  });

  // Export hook with UX callbacks
  const exportUnreachedStatusAnalytics = useExportUnreachedStatusAnalyticsRange({
    onSuccess: (data) => {
      toast.success(`Unreached status analytics exported successfully! File: ${data.filename}`);
    },
    onError: (error) => {
      toast.error(`Export failed: ${error.message}`);
    }
  });

  // Handle export button click
  const handleExport = () => {
    toast.loading('Preparing export...', { id: 'unreached-status-analytics-export' });

    exportUnreachedStatusAnalytics.mutate(localQueryParams, {
      onSettled: () => {
        toast.dismiss('unreached-status-analytics-export');
      }
    });
  };

  // Handle refresh button click
  const handleRefresh = () => {
    refetch();
  };

  // Transform API data to table format
  const transformDataForTable = (apiData) => {
    if (!apiData?.data?.breakdown) return [];

    return apiData.data.breakdown
      .filter(item => item.callStatus !== 'total') // Exclude total row from API
      .map((item, index) => ({
        serialNo: index + 1,
        callStatus: item.callStatus,
        newRegistration: item.newRegistration,
        recentlyDelivered: item.recentlyDelivered,
        followUp: item.followup || 0, // Use API followupTotal property with fallback to 0
        total: item.total
      }));
  };

  const tableData = useMemo(() => {
    return transformDataForTable(analyticsData);
  }, [analyticsData]);

  if (isError) {
    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        <Alert severity="error">
          Error loading unreached calls data: {error?.message || 'Unknown error'}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Stack direction={"column"} sx={{ mb: 2 }}>
        {/* Header with sync status */}
        <Stack direction="row" sx={{ mb: 2, justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="h3">
            Unreached Calls Analytics
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={isLocalOverride ? "Local Override" : "Synced with Global"}
              color={isLocalOverride ? "warning" : "success"}
              size="small"
              variant="outlined"
            />
            <Tooltip title={isLocalOverride ? "Reset to global sync" : "Enable local override"}>
              <IconButton onClick={toggleSync} size="small">
                {isLocalOverride ? <SyncDisabledIcon /> : <SyncIcon />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Controls */}
        <Stack direction={"row"} sx={{ mb: 2, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Stack direction="row" spacing={2}>
            <DateRangePicker
              label="Unreached Calls - Date Range"
              fromDate={localFromDate}
              toDate={localToDate}
              onFromChange={handleLocalFromDateChange}
              onToChange={handleLocalToDateChange}
              disabled={isFetching || exportUnreachedStatusAnalytics.isLoading}
              sx={{
                minWidth: 300,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1
                }
              }}
            />
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="unreached-data-type-select-label">Data Type</InputLabel>
              <Select
                labelId="unreached-data-type-select-label"
                id="unreached-data-type-select"
                value={localDataType}
                label="Data Type"
                onChange={handleLocalDataTypeChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1
                  }
                }}
              >
                {DATA_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.label} value={option.label}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Refresh and Export Buttons */}
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<RefreshOutlined />}
              onClick={handleRefresh}
              disabled={isFetching || exportUnreachedStatusAnalytics.isLoading}
              size="small"
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: '#f3f4f6',
                },
                '&:disabled': {
                  borderColor: '#e0e0e0',
                  color: '#9e9e9e',
                }
              }}
            >
              {isFetching ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadOutlined />}
              onClick={handleExport}
              disabled={isFetching || exportUnreachedStatusAnalytics.isLoading || !analyticsData?.data}
              size="small"
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: '#f3f4f6',
                },
                '&:disabled': {
                  borderColor: '#e0e0e0',
                  color: '#9e9e9e',
                }
              }}
            >
              {exportUnreachedStatusAnalytics.isLoading ? 'Exporting...' : 'Export'}
            </Button>
          </Stack>

          {/* Current filter display */}
          <Stack sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              Active Filter: <strong>{localDataType}</strong>
            </Typography>
            {isLocalOverride && (
              <Typography variant="caption" color="warning.main">
                (Overriding Global: {globalDataType})
              </Typography>
            )}
          </Stack>
        </Stack>
      </Stack>

      {isFetching ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DynamicTable
          title={`Unreached Calls Analytics - ${localFromDate.format("MMM DD, YYYY")} to ${localToDate.format("MMM DD, YYYY")} (${localDataType})`}
          data={tableData}
          sortable={true}
          showTotals={true}
        />
      )}
    </Paper>
  );
};